/**
 * Done. / ChiroCPD — Dashboard Page (/dashboard)
 *
 * Stitch v2 restyle (reference: .stitch/7._dashboard_v2/code.html).
 * Data still sourced from GET /api/dashboard with no schema changes.
 */

import { useState, useEffect } from 'react';
import { Link } from 'wouter';
import { AppLayout } from '@/layouts/AppLayout';
import { HourTracker } from '@/components/dashboard/HourTracker';
import { AnnualPlan } from '@/components/dashboard/AnnualPlan';
import { CompletedModules } from '@/components/dashboard/CompletedModules';
import { MyCertificates } from '@/components/dashboard/MyCertificates';
import type { CertEntry } from '@/components/dashboard/MyCertificates';
import { ExternalCPDCard } from '@/components/dashboard/ExternalCPDCard';
import { api, ApiError } from '@/lib/api';
import type { DashboardData } from '@/lib/types';

function DashboardContent(): React.ReactElement {
  const [data, setData] = useState<DashboardData | null>(null);
  const [certs, setCerts] = useState<CertEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function refresh(): Promise<void> {
    try {
      const result = await api.get<DashboardData>('/api/dashboard');
      setData(result);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return;
      setError(err instanceof Error ? err.message : 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    async function fetchAll(): Promise<void> {
      try {
        const [dashResult, certsResult] = await Promise.all([
          api.get<DashboardData>('/api/dashboard'),
          api.get<{ certs: CertEntry[] }>('/api/modules/certs'),
        ]);
        if (!cancelled) {
          setData(dashResult);
          setCerts(certsResult.certs);
        }
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) return;
        setError(err instanceof Error ? err.message : 'Failed to load dashboard');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void fetchAll();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <svg
          className="h-8 w-8 animate-spin text-[var(--stitch-primary)]"
          fill="none"
          viewBox="0 0 24 24"
          aria-label="Loading dashboard…"
        >
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="rounded-xl bg-[var(--stitch-surface-container-low)] border border-[var(--stitch-outline-variant)] px-5 py-4 text-sm text-[var(--stitch-on-surface)] flex items-center justify-between gap-4"
        role="alert"
      >
        <span>{error}</span>
        <button
          type="button"
          onClick={() => { window.location.reload(); }}
          className="stitch-font-label text-[12px] text-[var(--stitch-primary)] uppercase tracking-wider hover:underline"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!data) return <></>;

  const { capStatus, recentActivities, annualPlan } = data;

  return (
    <div className="flex flex-col gap-16">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div className="max-w-2xl">
          <span className="stitch-font-label text-[12px] text-[var(--stitch-surface-tint)] tracking-[0.2em] uppercase mb-3 block">
            Professional Development Dashboard
          </span>
          <h1 className="stitch-font-display text-[30px] md:text-[34px] font-bold text-[var(--stitch-primary)] leading-tight tracking-tight">
            Your Learning Progress
          </h1>
        </div>
        <Link
          href="/export"
          className="bg-[var(--stitch-primary)] text-[var(--stitch-on-primary)] px-8 py-4 rounded-full stitch-font-label text-[13px] tracking-[0.15em] uppercase hover:bg-[var(--stitch-primary-container)] transition-all duration-300 inline-flex items-center gap-3 stitch-editorial-shadow font-bold whitespace-nowrap"
        >
          <span className="material-symbols-outlined text-lg">picture_as_pdf</span>
          Export Year-End Report
        </Link>
      </div>

      {/* Hero: External CPD agent — full width, the primary action */}
      <div className="mx-auto w-full max-w-3xl">
        <ExternalCPDCard onActivityCreated={() => { void refresh(); }} />
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        {/* Left column */}
        <div className="md:col-span-4 flex flex-col gap-8">
          <HourTracker capStatus={capStatus} />
        </div>

        {/* Right column */}
        <div className="md:col-span-8 flex flex-col gap-10">
          <CompletedModules activities={recentActivities} />
          <MyCertificates certs={certs} />
        </div>
      </div>

      {/* Annual Plan — full width */}
      <AnnualPlan capStatus={capStatus} annualPlan={annualPlan} />

      {/* Board Pack link */}
      <div className="pt-8 border-t border-[var(--stitch-surface-container-high)] flex justify-center">
        <Link
          href="/export"
          className="bg-white border-2 border-[var(--stitch-primary)] text-[var(--stitch-primary)] px-10 py-4 rounded-full stitch-font-label text-[13px] tracking-[0.15em] uppercase hover:bg-[var(--stitch-primary)]/5 transition-all duration-300 inline-flex items-center gap-3 stitch-editorial-shadow font-bold"
        >
          <span className="material-symbols-outlined">folder_zip</span>
          Generate Board Pack
        </Link>
      </div>
    </div>
  );
}

export function DashboardPage(): React.ReactElement {
  return (
    <AppLayout currentRoute="/dashboard">
      <DashboardContent />
    </AppLayout>
  );
}
